// Core module: This should only depend on other core modules.

import type { NonNever } from 'ts-essentials';
import { check, checkPresent, CustomError } from './core/core.ts';
import type { Absent, FunctionType, Present, RecordKey } from './identity/core.ts';
import { ownKeys } from './identity/stronger_builtins.ts';
import type { TypesEqual } from './identity/type_equality.ts';
import type { Objectish } from './identity/type_guards.ts';
import { isKeyOf, isObject, isString } from './identity/type_guards.ts';

export type { NonNever } from 'ts-essentials';
export * from './identity/core.ts';
export type { Present } from './identity/core.ts';
export * from './identity/options.ts';
export * from './identity/records.ts';
export * from './identity/stronger_builtins.ts';
export * from './identity/type_equality.ts';
export * from './identity/type_guards.ts';

export function isEmpty(obj: unknown): boolean {
  // eslint-disable-next-line eqeqeq
  return obj != null && Object.keys(obj).length === 0;
}

export class ReadOnlyError extends CustomError {}

const readOnlyProxyTag: unique symbol = Symbol('ReadOnlyProxy');

const readOnlyProxy: ProxyHandler<object> = {
  apply(): never {
    throw new ReadOnlyError('Method calls are disallowed on readOnly() objects');
  },
  defineProperty(): never {
    throw new ReadOnlyError('Defining properties is disallowed on readOnly() objects');
  },
  deleteProperty(): never {
    throw new ReadOnlyError('Deleting properties is disallowed on readOnly() objects');
  },
  set(): never {
    throw new ReadOnlyError('Setting properties is disallowed on readOnly() objects');
  },
  setPrototypeOf(): never {
    throw new ReadOnlyError('Setting the prototype is disallowed on readOnly() objects');
  },
  has(target: object, p: string | symbol): boolean {
    return p === readOnlyProxyTag ? true : p in target;
  },
};

export function identity<T>(v: T) {
  return v;
}

export function maybe<T, U>(value: T, fn: (v: Present<T>) => U): U | Absent<T> {
  // eslint-disable-next-line eqeqeq
  return value != null ? fn(value) : (value as Absent<T>);
}

// TODO: Consider a `deepReadOnly` version.
export function readOnly<T extends object>(v: T): Readonly<T> {
  return Object.isFrozen(v) || Object.prototype.hasOwnProperty.call(v, readOnlyProxyTag)
    ? v
    : (new Proxy(v, readOnlyProxy) as Readonly<T>);
}

// https://stackoverflow.com/a/77888478
export type Implements<T, U extends T> = U;
export type Extends<T, U extends Pick<T, keyof T & keyof U>> = T & U;
export type Satisfies<_T extends U, U> = U;

// TODO: A bunch of these should be moved to a non-default-exported identity/subtle.ts with more
// in-depth documentation about how and why to use them.
export function getPrototypeChainOf(o: unknown) {
  // https://stackoverflow.com/a/70629468/8107589
  const result = [];
  // if (!(o instanceof Object)) {
  //   o = Reflect.getPrototypeOf(o);
  // }
  while (isObject(o) && o !== Object.prototype) {
    result.push(o);
    o = Reflect.getPrototypeOf(o);
  }
  return result;
}

// This provides the original `obj`, except it is typed as though it has a property of key `K` of
// type `V`. Can be used to access protected or private members of an object. However, there is no
// type validation on K or V (which ideally would be constrainted to keyof T and T[K],
// respectively).
export function exposeProperty<T extends object, K extends string, V>(
  obj: T,
): Omit<T, K> & { [k in K]: V } {
  return obj as unknown as Omit<T, K> & { [k in K]: V };
}

export function getPropertyDescriptor<T extends object>(
  obj: T,
  key: keyof T,
): undefined | { descriptor: PropertyDescriptor; prototype: object } {
  for (const prototype of getPrototypeChainOf(obj)) {
    const descriptor = Object.getOwnPropertyDescriptor(prototype, key);
    if (descriptor) {
      return { descriptor, prototype };
    }
  }
  // let prototype: object | null = obj;
  // while (prototype !== null && prototype !== Object) {
  //   const descriptor = Object.getOwnPropertyDescriptor(prototype, key);
  //   if (descriptor) {
  //     return { descriptor, prototype };
  //   }
  //   prototype = Object.getPrototypeOf(prototype) as object | null;
  // }
  return undefined;
}

export function makePropertyReadonly<T extends object, K extends keyof T>(
  obj: T,
  key: K,
  value?: T[K],
) {
  const prop = checkPresent(getPropertyDescriptor(obj, key));
  if (value !== undefined) {
    prop.descriptor.value = value;
  }
  prop.descriptor.writable = false;
  Object.defineProperty(prop.prototype, key, prop.descriptor);
}

// Modifies `obj[key]` so that all reads and writes to that property are filtered through `get` and
// `set`, respectively. Unlike `Proxy`, this mutates the original object's property descriptor.
// Call the returned function to restore the original descriptor.
// Here's how to wrap a method. TODO: Make the API easier for doing so idiomatically.
// wrapProperty(obj, 'method', {
//   get: (method) =>
//     function (arg: argType) {
//       console.log('method called');
//       method.call(obj, arg);
//     },
//   set: identity,
// });
export function wrapProperty<T extends object, K extends keyof T>(
  obj: T,
  key: K,
  config: {
    get: (actual: T[K]) => T[K];
    set: (intended: T[K]) => T[K];
    allowUndefined?: boolean;
  },
) {
  let prop = getPropertyDescriptor(obj, key);
  if (prop === undefined) {
    check(
      config.allowUndefined === true,
      `The object does not have property ${String(
        key,
      )}. If the property can be undefined, set allowUndefined.`,
    );
    // No descriptor probably means it's currently undefined, so let's create it as a value on the
    // base object. TODO: Our guess about the missing prototype could be wrong.
    prop = {
      descriptor: {
        configurable: true,
        value: undefined,
      },
      prototype: obj,
    };
    Object.defineProperty(prop.prototype, key, prop.descriptor);
  }
  const pd = prop.descriptor;
  const rawRead = () => (pd.get ? pd.get.call(obj) : pd.value) as T[K];
  const rawWrite = (v: T[K]) => {
    if (pd.set) {
      pd.set.call(obj, v);
    } else {
      pd.value = v;
    }
  };
  const wrapper: PropertyDescriptor = {
    get: () => config.get(rawRead()),
    set: (v: T[K]) => {
      rawWrite(config.set(v));
    },
  };
  Object.defineProperty(prop.prototype, key, wrapper);
  const prototype = prop.prototype;
  return () => {
    Object.defineProperty(prototype, key, pd);
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFunction = (...args: any) => any;

type MethodFromKeys<T, K extends keyof T> = {
  [P in K]-?: T[P] extends AnyFunction ? T[P] : never;
}[K];

export function wrapMethod<T, K extends keyof T, M extends MethodFromKeys<T, K>>(
  obj: T,
  key: K,
  config: {
    onCall?: (actual: Parameters<M>) => Parameters<M>;
    onReturn?: (intended: ReturnType<M>) => ReturnType<M>;
  },
) {
  const onCall = config.onCall ?? identity;
  const onReturn = config.onReturn ?? identity;
  const original = obj[key] as M;
  obj[key] = function (...args: Parameters<M>) {
    args = onCall(args);

    const result = original.apply(obj, args) as ReturnType<M>;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return onReturn(result);
  } as T[K];
  return () => {
    obj[key] = original;
  };
}

export type PropertyType = 'attribute' | 'accessor' | 'method';

export function getPropertyType<T extends object>(obj: T, key: keyof T) {
  let isOriginalObject = true;
  for (const prototype of getPrototypeChainOf(obj)) {
    const descriptor = Object.getOwnPropertyDescriptor(prototype, key);
    if (descriptor) {
      if (descriptor.get || descriptor.set) {
        return 'accessor';
      }
      return isOriginalObject ? 'attribute' : 'method';
    }
    isOriginalObject = false;
  }
  return undefined;
}

export interface KeysOfOptions {
  // Whether to include keys from the prototype chain, as opposed to only `value`.
  // Note, even when `true`, keys of `Object` are not included.
  readonly includePrototypes?: boolean;
  // Whether to include keys that start with `__`.
  readonly includeInternal?: boolean;
  // If non-empty, only properties of a matching type are included.
  readonly filterTypes?: readonly PropertyType[];
  // These keys are emitted before all other keys.
  readonly prioritizedKeys?: readonly RecordKey[];
}

// TODO: Rewrite this description.
// Iterate over all of the property keys and values in `obj` and its prototype chain, except for the
// root `Object` properties. Keys in `prioritizedKeys` appear first is the result. Remaining keys
// appear in an arbitrary order, except keys of object "x" appear before keys of "x"'s prototype.
// Keys that begin with '__' are skipped.
export function keysOf<T extends object>(value: T, options?: KeysOfOptions) {
  // https://stackoverflow.com/a/70629468/8107589
  const includePrototypes = options?.includePrototypes ?? false;
  const includeInternal = options?.includeInternal ?? false;
  const prioritizedKeys = options?.prioritizedKeys ?? [];
  const filterTypes: ReadonlyArray<PropertyType | undefined> = options?.filterTypes ?? [
    'attribute',
  ];

  const filters: Array<(k: keyof T & string) => boolean> = [];
  if (!includeInternal) {
    filters.push((k) => !k.startsWith('__'));
  }
  if (filterTypes.length) {
    filters.push((k) => filterTypes.includes(getPropertyType(value, k)));
  }
  if (prioritizedKeys.length) {
    filters.push((k) => !prioritizedKeys.includes(k));
  }

  const chain = includePrototypes ? getPrototypeChainOf(value) : [value];
  const results: Array<Array<keyof T & RecordKey>> = chain.map((v) => {
    return ownKeys(v).filter((k) => isString(k) && filters.every((f) => f(k)));
  }) as Array<Array<keyof T & string>>;
  results.unshift(prioritizedKeys.filter(isKeyOf(value)));
  return results.flat();
}

// export function* allNonObjectEntries(
//   obj: object,
//   ...prioritizedKeys: string[]
// ): IterableIterator<[string, unknown]> {
//   const record = obj as Record<string, unknown>;
//   for (const k of prioritizedKeys ?? []) {
//     if (!(k in record)) {
//       continue;
//     }
//     try {
//       const v = record[k];
//       yield [k, v];
//     } catch {
//       // Skip
//     }
//   }
//   for (const prototype of getPrototypeChainOf(obj)) {
//     for (const k of Object.getOwnPropertyNames(prototype)) {
//       if (k.startsWith('__') || prioritizedKeys?.includes(k)) {
//         continue;
//       }
//       try {
//         const v = record[k];
//         yield [k, v];
//       } catch {
//         // Skip
//       }
//     }
//   }
// }

// N.B. This doesn't actually coerce the result in a useful way.
// export function setTag<O extends object, T extends PropertyKey, V>(
//   obj: O,
//   tag: T,
//   value: V
// ): asserts obj is O & { get [typeof tag](): true } {
//   Object.defineProperty(obj, tag, { value });
// }

// http://stackoverflow.com/questions/40597658/equivalent-of-angular-equals-in-angular2
export function equals<T>(x: T, y: T, shallow = false): boolean {
  if (x === y) {
    return true;
  }
  if (!isObject(x) || !isObject(y) || x.constructor !== y.constructor) {
    // Catch the case of nothing being equal to NaN.
    return typeof x === 'number' && typeof y === 'number' && Number.isNaN(x) && Number.isNaN(y);
  }
  if (x instanceof Date && y instanceof Date) {
    return x.getTime() === y.getTime();
  }
  // TODO: Consider special cases for RegExp and other fanciness at:
  // https://stackoverflow.com/a/1144249/8107589
  return (
    Object.keys(x).every((k) => k in y && (shallow ? x[k] === y[k] : equals(x[k], y[k]))) &&
    Object.keys(y).every((k) => k in x)
  );
}

// Returns the subset of |value| with |keys|.
export function extract<T extends object>(value: T, ...keys: Array<keyof T>): Partial<T> {
  const result: Partial<T> = {};
  keys.forEach((k) => {
    result[k] = value[k];
  });
  return result;
}

export function getPath(path: string, value: Objectish): unknown {
  const keys = path.split('.');
  const final = keys.pop();
  if (final === undefined) {
    return undefined;
  }
  let current: Objectish | undefined = value;
  keys.forEach((key) => {
    const next = current?.[key];
    current = isObject(next) ? next : undefined;
  });
  return current[final];
}

// Annotate methods that override base class for more type safety.
// https://github.com/microsoft/TypeScript/issues/2000#issuecomment-520399260
// Usage:
//   class Base { foo(a: number): boolean {} }
//   class Derived extends Base { @override(Base) foo(a: number): boolean{} }
export const Override =
  <Sup, Field extends keyof Sup, Proto extends { [key in Field]: Sup[Field] }>(_sup: {
    prototype: Sup;
  }): ((_proto: Proto, _field: Field, _descr: TypedPropertyDescriptor<Sup[Field]>) => void) =>
  (_proto, _field, _descr): void => {
    // This space intentionally left blank.
  };

// Same as `T`, but without properties whose types extend `PT`.
export type TypedPropertiesOf<T, P> = {
  [K in keyof T as T[K] extends P ? K : never]: T[K];
};
// export type TypedPropertiesOf<T, PT> = {
//   [P in keyof T]: T[P] extends PT ? T[P] : never;
// };

export type MethodOf<T> = keyof NonNever<TypedPropertiesOf<T, FunctionType>>;

export type PrefixOf<T, S extends string> = T extends `${infer Prefix}${S}` ? Prefix : never;
export type SuffixOf<T, P extends string> = T extends `${P}${infer Suffix}` ? Suffix : never;

// Declare an array whose elements fully cover the range of union type T.
// Example:
//   type Letters = 'a' | 'b' | 'c';
//   const letters = rangeOf<Letters>()(['a', 'b', 'c']);
// Alternate approach: https://www.reddit.com/r/typescript/comments/n1iiyp/comment/gwdjaun/?utm_source=share&utm_medium=web2x&context=3
//   const letters = ['a', 'b', 'c'];
//   type Letters = typeof letters[number];
export function rangeOf<U>() {
  return function <T extends U[]>(...values: T) {
    // Note: I considered sorting and de-duping `values` here, but the point of this
    // function is to ensure full-coverage of the range of T, not to ensure it's a canonical
    // representation. It's not clear that the runtime cost of sorting/filtering is worthwhile.
    type Result = [true] extends [TypesEqual<U, (typeof values)[number]>] ? T : never;
    return values as Result;
  };
}

// Note: Not really useful because we can't even access the types of protected or private members.
// export function getPropertyUnsafe<T extends object>(obj: T, k: keyof T): T[typeof k] {
//   return obj[k];
// }
export function getPropertyUnsafe<T>(obj: object, k: string): T | undefined {
  return Object.prototype.hasOwnProperty.call(obj, k)
    ? (obj as Record<string, T | undefined>)[k]
    : undefined;
}
